import { getAceBackgroundImageNames } from "./ace-background-image.get-names";
import { h, Host } from "@stencil/core";
import { Config } from "../../utils/config/config";
export class AceBackgroundImage {
    constructor() {
        this.onIntersection = (entries) => {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    /* Do not observe image anymore */
                    if (this.observer) {
                        this.observer.disconnect();
                    }
                    /* Set src attribute of image & remove placeholder */
                    if (!entry.target.classList.contains(this.names.CLASS.IMAGE____LOADED)) {
                        entry.target.setAttribute('src', this.src);
                    }
                }
            }
        };
        this.src = undefined;
        this.fallbackSrc = undefined;
        this.placeholderSrc = undefined;
        this.withOverlay = false;
        this.withBlur = false;
        this.lazy = true;
        this.shouldShowImage = undefined;
    }
    componentWillLoad() {
        var _a;
        this.names = getAceBackgroundImageNames((_a = this.hostElement) === null || _a === void 0 ? void 0 : _a.id);
        this.shouldShowImage = !this.lazy && !this.placeholderSrc;
    }
    // We have to use the componentDidRender method because we want this to execute everytime the component is rendered.
    // This is done to re-render the image when the src (or any other) property changes, even when it's lazy loaded.
    componentDidRender() {
        if (this.lazy && !this.observer) {
            if (this.imgEl) {
                this.observer = new IntersectionObserver(this.onIntersection);
                this.observer.observe(this.imgEl);
            }
        }
    }
    getImgProperties(forPlaceholder = false) {
        let imageFields = {
            alt: '',
            src: this.lazy ? '' : this.src,
            class: {
                [this.names.CLASS.IMAGE]: true,
                [this.names.CLASS.IMAGE____PLACEHOLDER]: forPlaceholder,
                [this.names.CLASS.IMAGE____ANIMATION_DISABLED]: Config.isTimersAndAnimationsDisabled(),
            }
        };
        if (!this.lazy) {
            imageFields.src = this.src;
        }
        if (!forPlaceholder) {
            if (this.fallbackSrc) {
                imageFields['onerror'] = () => {
                    if (this.imgEl.src !== this.fallbackSrc) {
                        this.imgEl.setAttribute('src', this.fallbackSrc);
                    }
                };
            }
            imageFields['onload'] = () => {
                this.imgEl.classList.add(this.names.CLASS.IMAGE____LOADED);
                if (!this.shouldShowImage && (this.lazy || this.placeholderSrc)) {
                    this.shouldShowImage = true;
                }
            };
        }
        return imageFields;
    }
    renderPlaceholder() {
        if (this.placeholderSrc && !this.shouldShowImage) {
            const imgProperties = this.getImgProperties(true);
            imgProperties.src = this.placeholderSrc;
            return h("img", Object.assign({}, imgProperties));
        }
    }
    render() {
        let backgroundClasses = {
            [this.names.CLASS.CONTAINER]: true,
            [this.names.CLASS.CONTAINER____BLUR]: this.withBlur,
            [this.names.CLASS.CONTAINER____OVERLAY]: this.withOverlay,
        };
        const imageFields = this.getImgProperties();
        return (h(Host, { key: 'd90d1a883ca0ec1931826566a2821d720612daad' }, h("div", { key: '05bfa39aa58b16c1607622dce25536f05e2dc45d', class: backgroundClasses }, this.renderPlaceholder(), this.src && h("img", Object.assign({ key: '366a57da14bf7918193aa1d63c71eb4672cb60cb' }, imageFields, { ref: (el) => this.imgEl = el }))), h("div", { key: '91195ccb04ed50ee6313c7ae3c3a1b189fd12cc1', class: this.names.CLASS.CONTENT }, h("slot", { key: 'd22a9242f200e6127594fdfb37fd4efe0d3bb98b' }))));
    }
    static get is() { return "ace-background-image"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["./ace-background-image.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["ace-background-image.css"]
        };
    }
    static get properties() {
        return {
            "src": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "src",
                "reflect": false
            },
            "fallbackSrc": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "fallback-src",
                "reflect": false
            },
            "placeholderSrc": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "placeholder-src",
                "reflect": false
            },
            "withOverlay": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "with-overlay",
                "reflect": false,
                "defaultValue": "false"
            },
            "withBlur": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "with-blur",
                "reflect": false,
                "defaultValue": "false"
            },
            "lazy": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "lazy",
                "reflect": false,
                "defaultValue": "true"
            }
        };
    }
    static get states() {
        return {
            "shouldShowImage": {}
        };
    }
    static get elementRef() { return "hostElement"; }
}
//# sourceMappingURL=ace-background-image.js.map
